/**
 * Wall Panel — public room hub for any iPad / Android tablet.
 *
 * Auth: the panel holds the pair_token issued by the admin once. We poll
 * /api/panel/snapshot every 30s for the calm zone view. Token is persisted
 * in localStorage as `panel_pair_token`.
 *
 * Route: /panel
 */
import { useEffect, useState, useCallback } from "react";
import { Sparkles, MapPin, Heart } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const STATE_LABEL = {
  arrived: "settled in",
  learning: "in the yard",
  playing: "at play",
  resting: "resting",
  eating: "sharing a meal",
  safe: "in care",
  with_family: "with family",
  away: "away",
};

function fmtTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function fmtClock(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

export default function PanelHub() {
  const [token, setToken] = useState(
    () => localStorage.getItem("panel_pair_token") || ""
  );
  const [input, setInput] = useState("");
  const [snap, setSnap] = useState(null);
  const [error, setError] = useState(null);

  const fetchSnapshot = useCallback(async () => {
    if (!token) return;
    try {
      const r = await fetch(`${API}/api/panel/snapshot`, {
        headers: { "X-Alouvya-Pair-Token": token },
      });
      if (r.status === 403) {
        setError("Pair token rejected — ask your admin for a fresh one.");
        return;
      }
      if (!r.ok) {
        setError(`Snapshot failed (${r.status})`);
        return;
      }
      setError(null);
      setSnap(await r.json());
    } catch (e) {
      setError("Connection paused.");
    }
  }, [token]);

  useEffect(() => {
    fetchSnapshot();
    if (!token) return;
    const id = setInterval(fetchSnapshot, 30_000);
    return () => clearInterval(id);
  }, [token, fetchSnapshot]);

  if (!token) {
    return (
      <PairingScreen
        input={input}
        setInput={setInput}
        onConfirm={() => {
          if (!input.trim()) return;
          localStorage.setItem("panel_pair_token", input.trim());
          setToken(input.trim());
        }}
      />
    );
  }

  if (error) {
    return (
      <ErrorScreen
        error={error}
        onReset={() => {
          localStorage.removeItem("panel_pair_token");
          setToken("");
          setInput("");
        }}
      />
    );
  }

  if (!snap) {
    return (
      <div className="min-h-screen bg-[#0e1f17] text-[#E0DED9] flex items-center justify-center">
        <p className="font-serif italic">A quiet moment…</p>
      </div>
    );
  }

  const zone = snap.zone;
  const present = snap.children_in_zone || [];

  return (
    <div
      data-testid="panel-hub"
      className="min-h-screen bg-[#0e1f17] text-[#E0DED9] px-10 py-10 flex flex-col"
    >
      {/* Top — workspace + clock */}
      <header className="flex items-baseline justify-between">
        <div>
          <p className="text-[10px] tracking-[0.32em] uppercase text-[#8A9984]">
            {snap.workspace?.name || "Alouvya"}
          </p>
          <h1
            data-testid="panel-zone-name"
            className="mt-2 font-serif text-5xl tracking-tight"
          >
            {zone?.name || snap.device.label}
          </h1>
        </div>
        <div className="text-right">
          <p className="text-[10px] tracking-[0.32em] uppercase text-[#8A9984]">
            Right now
          </p>
          <p
            data-testid="panel-clock"
            className="mt-2 font-serif text-4xl tracking-tight tabular-nums"
          >
            {fmtClock(snap.now)}
          </p>
        </div>
      </header>

      {/* Body — present children */}
      <main className="mt-12 flex-1">
        <div className="flex items-baseline justify-between">
          <p className="text-[10px] tracking-[0.32em] uppercase text-[#8A9984]">
            Here with us · {present.length}
            {zone?.capacity ? ` of ${zone.capacity}` : ""}
          </p>
          {zone?.kind && (
            <p className="text-[10px] tracking-[0.32em] uppercase text-[#8A9984]">
              {zone.kind.replace("_", " ")}
            </p>
          )}
        </div>
        {present.length === 0 ? (
          <p
            data-testid="panel-empty"
            className="mt-10 font-serif italic text-[#8A9984] text-2xl"
          >
            All quiet in {zone?.name || "this room"} just now.
          </p>
        ) : (
          <ul
            data-testid="panel-children"
            className="mt-8 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-5"
          >
            {present.map((c) => (
              <li
                key={c.id}
                data-testid={`panel-child-${c.id}`}
                className="rounded-3xl bg-[#152e22] border border-[#8A9984]/10 p-5 flex items-start gap-3"
              >
                {c.portrait_url ? (
                  <img
                    src={c.portrait_url}
                    alt=""
                    className="h-12 w-12 rounded-2xl object-cover shrink-0"
                  />
                ) : (
                  <span
                    aria-hidden
                    className="h-12 w-12 rounded-2xl bg-[#1d3a2c] flex items-center justify-center text-2xl text-[#E0DED9] shrink-0"
                  >
                    {c.name?.[0] || "—"}
                  </span>
                )}
                <div className="min-w-0">
                  <p className="font-serif text-xl text-[#E0DED9] truncate">
                    {c.name}
                  </p>
                  <p className="text-[10px] tracking-[0.18em] uppercase text-[#8A9984] mt-1">
                    {STATE_LABEL[c.current_state] || "in care"}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </main>

      {/* Footer — whispers */}
      {snap.whispers?.length > 0 && (
        <footer className="mt-10 pt-6 border-t border-[#8A9984]/15">
          <p className="text-[10px] tracking-[0.32em] uppercase text-[#8A9984] flex items-center gap-2">
            <Sparkles size={11} strokeWidth={1.6} />
            Gentle whispers
          </p>
          <ul
            data-testid="panel-whispers"
            className="mt-3 space-y-1.5 text-sm text-[#E0DED9]"
          >
            {snap.whispers.slice(0, 4).map((w, i) => (
              <li key={i} className="flex gap-3">
                <span className="text-[#8A9984] tabular-nums shrink-0">
                  {fmtTime(w.at)}
                </span>
                <span className="text-[#E0DED9]/85">
                  {w.child_name} · {STATE_LABEL[w.status] || w.status}
                </span>
                <span className="text-[#8A9984] truncate">· by {w.by}</span>
              </li>
            ))}
          </ul>
        </footer>
      )}
    </div>
  );
}

function PairingScreen({ input, setInput, onConfirm }) {
  return (
    <div
      data-testid="panel-pairing"
      className="min-h-screen bg-[#0e1f17] text-[#E0DED9] flex items-center justify-center p-8"
    >
      <div className="max-w-md w-full">
        <p className="text-[10px] tracking-[0.32em] uppercase text-[#8A9984]">
          Wall panel
        </p>
        <h1 className="mt-3 font-serif text-4xl tracking-tight">
          Welcome the room.
        </h1>
        <p className="mt-4 text-sm text-[#8A9984] leading-relaxed">
          Paste the calm pair token your admin generated. We'll remember this
          device until you reset it.
        </p>
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          data-testid="panel-pair-input"
          placeholder="pair token"
          className="mt-8 w-full bg-[#152e22] border border-[#8A9984]/15 rounded-2xl px-5 py-4 text-[#E0DED9] outline-none focus:border-[#8A9984]/40 font-mono text-xs"
        />
        <button
          type="button"
          onClick={onConfirm}
          data-testid="panel-pair-confirm"
          className="mt-5 w-full rounded-2xl bg-[#8A9984] text-[#0e1f17] py-3 text-xs tracking-[0.32em] uppercase font-medium hover:bg-[#9aaa92] transition-colors"
        >
          Settle this panel
        </button>
      </div>
    </div>
  );
}

function ErrorScreen({ error, onReset }) {
  return (
    <div
      data-testid="panel-error"
      className="min-h-screen bg-[#0e1f17] text-[#E0DED9] flex items-center justify-center p-8"
    >
      <div className="max-w-md w-full text-center">
        <Heart
          size={36}
          strokeWidth={1.3}
          className="text-[#8A9984] mx-auto"
        />
        <h1 className="mt-6 font-serif text-3xl tracking-tight">
          A quiet hiccup.
        </h1>
        <p className="mt-3 text-sm text-[#8A9984]">{error}</p>
        <button
          type="button"
          onClick={onReset}
          data-testid="panel-reset"
          className="mt-8 rounded-full bg-[#8A9984] text-[#0e1f17] px-6 py-3 text-xs tracking-[0.32em] uppercase font-medium hover:bg-[#9aaa92]"
        >
          Pair again
        </button>
      </div>
    </div>
  );
}
